import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import axios from 'axios';
import {
  API_RESOLVE_FAILING_SN,
  INSTAGRAM_API,
  INSTAGRAM_ASSING_CLIENT
} from '../utils/constants';
import { useSelector } from 'react-redux';
import { authSelector } from '../store/slices/auth';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

const NewInstagramConnection = ({
  open,
  onOpenChange,
  currentClientId,
  currentClient,
  connectSnCallback
}) => {
  const { isAuthenticated, userInfo } = useSelector(authSelector);
  const [state, setState] = useState({
    optionsData: null,
    loaded: false,
    instagramPage: null,
    clientId: null,
    savingClient: false
  });
  const [isFailing, setIsFailing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleResolved = async (snName) => {
    let config = {
      method: 'post',
      url: API_RESOLVE_FAILING_SN,
      data: { clientId: currentClientId, snName },
      headers: { 'Content-Type': 'application/json' }
    };

    if (isFailing) {
      setIsLoading(true);
      await axios(config)
        .then(async (response) => {
          if (response.status === 200) {
            setIsFailing(false);
            setIsLoading(false);
            await someFn();
          }
        })
        .catch((error) => {
          console.error(error);
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    if (currentClient?.clientSocialNetworks?.igFailing !== undefined) {
      setIsFailing(currentClient?.clientSocialNetworks?.igFailing);
    } else {
      setIsFailing(false);
    }
  }, [currentClient, currentClientId]);

  useEffect(() => {
    async function getPages() {
      try {
        const response = await axios.get(INSTAGRAM_API, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const JPages = response.data;

        setState((prevState) => ({
          ...prevState,
          optionsData: JPages.IGpages,
          loaded: true
        }));
      } catch (error) {
        console.error('Error fetching pages:', error);
      }
    }
    if (state.optionsData === null) getPages();
    // eslint-disable-next-line
  }, []);

  const handleSelectChanges = (option) => {
    if (option) {
      const options = { id: option.value, entity_name: option.label };

      setState((prev) => ({
        ...prev,
        instagramPage: options,
        clientId: currentClientId
      }));
    }
  };

  const instagramPageConnectionHandler = async () => {
    setState((prev) => ({ ...prev, savingClient: true }));

    if (state.instagramPage == null) {
      alert('Please select a page.');
      setState((prev) => ({ ...prev, savingClient: false }));
      return;
    }

    const payload = {
      clientId: state.clientId,
      instagramPageId: state.instagramPage.id,
      instagramPageName: state.instagramPage.entity_name
    };

    try {
      const response = await axios.post(INSTAGRAM_ASSING_CLIENT, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log(response.data);

      setState((prev) => ({ ...prev, savingClient: false }));
      onOpenChange(false);
      someFn();
    } catch (error) {
      console.error('There was a problem with the axios request:', error);
      setState((prev) => ({ ...prev, savingClient: false }));
    }
  };

  const someFn = async () => {
    await connectSnCallback();
  };

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '44px',
      borderRadius: '0.5rem',
      borderColor: state.isFocused
        ? 'hsl(var(--primary))'
        : 'hsl(var(--border))',
      boxShadow: state.isFocused ? '0 0 0 1px hsl(var(--primary))' : 'none',
      '&:hover': {
        borderColor: 'hsl(var(--primary))'
      }
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '0.5rem',
      zIndex: 50
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'hsl(var(--primary))'
        : state.isFocused
        ? 'hsl(var(--accent))'
        : 'transparent',
      cursor: 'pointer'
    })
  };

  if (!state.loaded && !state.optionsData) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            Connect Instagram Account
          </DialogTitle>
          <DialogDescription>
            Select the Instagram page for{' '}
            {currentClient?.clientName || 'this client'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Instagram Page Selector */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">
                Instagram Business Page
              </Label>
              <span className="text-sm text-muted-foreground">
                {state.optionsData?.length || 0} pages available
              </span>
            </div>
            <Select
              options={state.optionsData?.map((option) => ({
                value: option.id,
                label: option.ig_name
              }))}
              onChange={handleSelectChanges}
              isSearchable
              isClearable
              placeholder="Search for an Instagram page..."
              isLoading={!state.optionsData}
              styles={customSelectStyles}
              className="react-select-container"
              classNamePrefix="react-select"
            />
            <p className="text-xs text-muted-foreground">
              Select the Instagram business account you want to connect
            </p>
          </div>

          {/* Failing Status Alert */}
          {isAuthenticated &&
            userInfo.role === 'superAdmin' &&
            isFailing &&
            currentClient?.clientSocialNetworks?.instagramPage && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-medium mb-1">
                      This Instagram connection is failing
                    </p>
                    <p className="text-xs">
                      Currently connected to:{' '}
                      {currentClient.clientSocialNetworks.instagramPage}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label
                      htmlFor="resolve-ig-checkbox"
                      className="text-xs cursor-pointer">
                      Mark as resolved
                    </label>
                    <input
                      id="resolve-ig-checkbox"
                      type="checkbox"
                      className="h-4 w-4 cursor-pointer"
                      onChange={() => handleResolved('instagram')}
                      checked={!isFailing}
                      disabled={isLoading}
                    />
                    {isLoading && (
                      <Loader2 className="h-4 w-4 animate-spin ml-1" />
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

          {/* Success indicator when page is selected */}
          {state.instagramPage && (
            <Alert>
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription>
                Selected: <strong>{state.instagramPage.entity_name}</strong>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={state.savingClient}>
            Cancel
          </Button>
          <Button
            onClick={instagramPageConnectionHandler}
            disabled={state.savingClient || !state.instagramPage}
            className="min-w-[120px]">
            {state.savingClient ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Connecting...
              </>
            ) : (
              'Connect Page'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewInstagramConnection;
